/**
 * worklistUI.js — окно «Рабочий список»: исследования на подключённом PACS за
 * день или диапазон, с фильтрами по модальности и пациенту; из строки —
 * загрузить серию или сразу открыть «Прошлые» этого пациента.
 *
 * ЧЕСТНОСТЬ НАЗВАНИЯ. Это НЕ расписание из RIS: настоящий worklist (MWL) — это
 * DIMSE C-FIND, браузеру недоступный по построению (нет сырых сокетов). Здесь
 * QIDO-поиск по архиву, то есть ФАКТИЧЕСКИ ПРИНЯТОЕ на сервер, — окно говорит
 * об этом в подписи, а не делает вид, что читает расписание.
 *
 * Разбор ответов и построение запроса — чистый io/pacsExchange.js (проверен
 * tests/pacs_exchange_test.mjs: даты, диапазоны, имя против ID, сортировка по
 * времени). Загрузка серии — ОБЩИМ блоком с «Прошлыми с PACS»
 * (кнопкиСерийИсследования), правило загрузки одно.
 */

import { getClient } from '../io/dicomWebClient.js';
import { параметрыСписка, разобратьСписокДня, датаДень } from '../io/pacsExchange.js';
import { кнопкиСерийИсследования, открытьПрошлыеPACS } from './pacsCompareUI.js';
import { подобратьПроцедуру } from '../core/procedureLayout.js';   // [12.0.6]
import { применитьСценарий } from './sidebarBindings.js';           // [12.0.6] тем же правилом, что кнопка сценария
import { changeLayout } from './layoutManager.js';                  // [12.0.6]
import { окно, строкаВвода } from './quantTools.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const $ = (id) => document.getElementById(id);

function _послеЗагрузки() {
  import('./comparePanel.js').then((м) => м.onStudiesChanged?.()).catch((e) => log.debug('[Список] окна:', e.message));
}

async function _показать(client, тело) {
  const п = параметрыСписка({
    деньОт: String($('wl-от')?.value || '').trim(),
    деньДо: String($('wl-до')?.value || '').trim(),
    модальность: $('wl-мод')?.value || 'ВСЕ',
    пациент: $('wl-кто')?.value || '',
  });
  if (!п.ok) { showToast('Рабочий список: ' + п.причина, 'warning', 6000); return; }
  тело.innerHTML = '<div style="color:#9fb3cf;">Спрашиваю сервер…</div>';
  let строки;
  try {
    строки = разобратьСписокДня(await client.searchStudies(п.параметры));
  } catch (e) {
    тело.innerHTML = `<div style="color:#f2b8b5;">Сервер не ответил: ${e.message}</div>`;
    return;
  }
  if (!строки.length) {
    тело.innerHTML = '<div style="color:#9fb3cf;">За выбранный срок на сервере исследований не нашлось.</div>';
    return;
  }
  тело.innerHTML = `<div style="color:#9fb3cf;margin:4px 0;">Нашлось: ${строки.length}. Ранние сверху.</div>`;
  for (const ст of строки) {
    const строка = document.createElement('div');
    строка.style.cssText = 'display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap;'
      + 'border:1px solid #223047;border-radius:8px;padding:8px 10px;margin:6px 0;';
    const подпись = document.createElement('div');
    подпись.innerHTML = `<b>${ст.время || '—:—'}</b> · ${ст.пациент || ст.patientId || 'пациент без имени'}`
      + ` · ${ст.модальности.join('+') || '?'}${ст.кадров ? ` · ${ст.кадров} кадр.` : ''}`
      + (ст.описание ? `<div style="color:#9fb3cf;">${ст.описание}</div>` : '');
    const кнопки = document.createElement('div');
    кнопки.style.cssText = 'display:flex;gap:6px;';
    // [12.0.6] Раскладка под процедуру: подбор по словам описания. Подбор
    // ничего не навязывает — применяется только с галочкой врача; галочка
    // взводится сама лишь когда сценарий узнан уверенно (по слову).
    const подбор = подобратьПроцедуру(ст);
    const строкаПодбора = document.createElement('label');
    строкаПодбора.style.cssText = 'display:flex;align-items:center;gap:6px;width:100%;color:#9fb3cf;font-size:12px;';
    const чек = document.createElement('input');
    чек.type = 'checkbox';
    чек.checked = !!подбор.сценарий;
    строкаПодбора.append(чек, document.createTextNode(
      `после загрузки: раскладка ${подбор.раскладка.toUpperCase()}`
      + (подбор.сценарий ? ` + сценарий «${подбор.имя}» (${подбор.почему})` : ` (${подбор.почему})`)));

    const кнОткр = document.createElement('button');
    кнОткр.textContent = 'Открыть…';
    кнОткр.style.cssText = 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:6px 12px;cursor:pointer;';
    кнОткр.addEventListener('click', async () => {
      кнОткр.disabled = true; кнОткр.textContent = 'спрашиваю…';
      try {
        const блок = document.createElement('div');
        блок.style.cssText = 'width:100%;margin-top:6px;';
        await кнопкиСерийИсследования(client, ст, блок, {
          послеЗагрузки: () => {
            _послеЗагрузки();
            if (!чек.checked) return;
            // Раскладка перестраивает окна; сценарий (окно W/L + 3D-пресет)
            // применяется по готовым окнам. Пауза — признанный компромисс:
            // события «раскладка перестроена» в программе нет, а заводить
            // шину ради одной паузы — не в этот заход. При промахе врач
            // нажмёт сценарий в панели «Пресеты» — правило применения одно.
            Promise.resolve(changeLayout(подбор.раскладка))
              .catch((e) => log.debug('[Список] раскладка:', e.message))
              .finally(() => {
                if (подбор.сценарий) setTimeout(() => {
                  try { применитьСценарий(подбор.сценарий); }
                  catch (e) { log.debug('[Список] сценарий:', e.message); }
                }, 900);
              });
          },
        });
        кнОткр.remove();
        строка.appendChild(блок);
      } catch (e) {
        кнОткр.disabled = false; кнОткр.textContent = 'Открыть…';
        showToast('Список серий не пришёл: ' + e.message, 'error', 7000);
      }
    });
    const кнПрош = document.createElement('button');
    кнПрош.textContent = 'Прошлые';
    кнПрош.title = 'Прошлые исследования ЭТОГО пациента на сервере';
    кнПрош.style.cssText = 'background:#1d2939;border:1px solid #33415a;color:#e7eefc;border-radius:8px;padding:6px 12px;cursor:pointer;';
    кнПрош.addEventListener('click', () => {
      if (!ст.patientId) { showToast('У записи нет PatientID — прошлые искать не по чему', 'warning', 5000); return; }
      открытьПрошлыеPACS({
        patientId: ст.patientId, patientName: ст.пациент,
        загруженныеUID: new Set([ст.studyUID]),
        послеЗагрузки: _послеЗагрузки,
      });
    });
    кнопки.append(кнОткр, кнПрош);
    строка.append(подпись, кнопки, строкаПодбора);
    тело.appendChild(строка);
  }
}

function _открытьСписок() {
  const client = getClient();
  if (!client) {
    showToast('Сервер PACS не подключён: кнопка «DICOMweb» в верхнем ряду — подключите и повторите', 'warning', 7000);
    return;
  }
  const w = окно('Рабочий список (по архиву PACS)', { ширина: 720 });
  const фильтры = document.createElement('div');
  фильтры.innerHTML = строкаВвода('С даты (ГГГГММДД)', 'wl-от', датаДень(0), 'style="width:110px"')
    + строкаВвода('По дату (пусто — один день)', 'wl-до', '', 'style="width:110px"')
    + `<label style="display:flex;align-items:center;gap:8px;margin:5px 0;">
        <span style="min-width:210px;color:#9fb3cf;">Модальность</span>
        <select id="wl-мод" style="background:#0d1420;color:#e7eefc;border:1px solid #33415a;border-radius:8px;padding:6px;">
          ${['ВСЕ', 'CT', 'MR', 'PT', 'US', 'XA', 'CR', 'DX', 'MG', 'NM'].map((м) => `<option>${м}</option>`).join('')}
        </select></label>`
    + строкаВвода('Пациент (имя или ID)', 'wl-кто', '', 'placeholder="иван / 123-45" style="width:160px"');
  const ряд = document.createElement('div');
  ряд.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin:6px 0;';
  const тело = document.createElement('div');
  w.тело.append(фильтры, ряд, тело);
  w.тело.insertAdjacentHTML('beforeend',
    '<div style="margin-top:8px;color:#9fb3cf;">Это поиск по АРХИВУ (QIDO), то есть фактически принятое на сервер, '
    + 'а не расписание из RIS: настоящий worklist (MWL) браузеру недоступен по построению.</div>');

  const кн = (текст, действие, главная = false) => {
    const б = document.createElement('button');
    б.textContent = текст;
    б.style.cssText = главная
      ? 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:6px 12px;cursor:pointer;'
      : 'background:#1d2939;border:1px solid #33415a;color:#e7eefc;border-radius:8px;padding:6px 12px;cursor:pointer;';
    б.addEventListener('click', действие);
    ряд.appendChild(б);
  };
  кн('Показать', () => _показать(client, тело), true);
  кн('Сегодня', () => { $('wl-от').value = датаДень(0); $('wl-до').value = ''; _показать(client, тело); });
  кн('Неделя', () => { $('wl-от').value = датаДень(7); $('wl-до').value = датаДень(0); _показать(client, тело); });

  _показать(client, тело);
}

export function initWorklist() {
  $('btn-worklist')?.addEventListener('click', () => {
    try { _открытьСписок(); } catch (e) { log.warn('[Список]', e); showToast('Рабочий список: ' + e.message, 'error', 7000); }
  });
  log.debug('[Список] Готово: рабочий список по QIDO');
}
